import type { Permission } from '@hbac/contract';
import {
  Bell, BookMarked, CalendarCheck, ClipboardCheck, SquareKanban, FileText, GitBranch, Home, List, Mail, PieChart, CirclePlus, Settings, type LucideIcon,
} from 'lucide-react';

/**
 * Route table.
 *
 * Split in two halves that are governed by different rules:
 *
 *   `publicPaths` — reachable with no account at all. Every addition here
 *   widens the surface anyone on the internet can reach, so the list is short
 *   and deliberate.
 *
 *   `paths` — the member area. Everything here redirects to the login screen
 *   when signed out.
 */
export const publicPaths = {
  home: '/',
  news: '/news',
  newsPage: '/news/pages/:page',
  newsDetail: '/news/:slug',
  competition: '/hbaac',
  join: '/join',
  login: '/login',
} as const;

export const paths = {
  portal: '/portal',
  dashboard: '/dashboard',
  /** The list of the khoản the member created (Plan BT); the menu's Thu/Chi opens it. */
  bills: '/bills',
  /** Writing a new khoản; the "+" buttons open it directly. */
  create: '/create-bill',
  approvals: '/approvals',
  settings: '/settings',
  posts: '/posts',
  postNew: '/posts/new',
  postEdit: '/posts/:postId/edit',
  mail: '/mail',
  campaign: '/mail/:campaignId',
  logs: '/logs',
  notifications: '/notifications',
  /** Now a tab of Cài đặt (Plan X4); the old address redirects there. */
  siteContent: '/site-content',
  siteContentTab: '/settings?tab=site',
  /** The directory is gone (Plan X2): this redirects to Thống kê; profiles stay under it. */
  members: '/members',
  memberProfile: '/members/:memberId',
  generations: '/generations',
  handover: '/generations/new',
  events: '/events',
  eventDetail: '/events/:eventId',
  eventPresent: '/events/:eventId/present',
  checkIn: '/checkin/:eventId',
  tasks: '/tasks',
  docs: '/docs',
  docNew: '/docs/new',
  doc: '/docs/:slug',
  docEdit: '/docs/:slug/edit',
} as const;

export type PathKey = keyof typeof paths;

export interface NavItem {
  key: PathKey;
  path: string;
  icon: LucideIcon;
  label: string;
  /** Routes needing a permission are hidden from the nav AND blocked by the guard. */
  permission?: Permission;
  /** Shown to holders of any of these; the page itself says what each may open. */
  anyPermission?: Permission[];
  /** Hidden from alumni (they settle old bills, they do not start new ones). */
  membersOnly?: boolean;
}

/**
 * Sidebar order, by how often it is used: yourself and what is new, the club's
 * work (tasks, events), money (bills, approvals, figures), people, then the
 * content and administration screens. The "Thêm" sheet on phones follows it.
 */
/**
 * Who sees Cài đặt in the menu: holders of a permission that opens one of its sections. Written out
 * here so the menu does not pull the sections' table into every page; a test holds it equal to theirs.
 */
export const SETTINGS_PERMISSIONS: Permission[] = ['members.manage', 'teams.manage', 'roles.manage', 'site.manage', 'settings.manage'];

export const NAV_ITEMS: NavItem[] = [
  { key: 'portal', path: paths.portal, icon: Home, label: 'Cá nhân' },
  { key: 'notifications', path: paths.notifications, icon: Bell, label: 'Thông báo' },
  { key: 'tasks', path: paths.tasks, icon: SquareKanban, label: 'Công việc', membersOnly: true },
  { key: 'events', path: paths.events, icon: CalendarCheck, label: 'Sự kiện', permission: 'events.manage' },
  // Alumni keep it: they follow the khoản they created before, they just cannot start a new one.
  { key: 'bills', path: paths.bills, icon: CirclePlus, label: 'Thu/Chi' },
  { key: 'approvals', path: paths.approvals, icon: ClipboardCheck, label: 'Duyệt' },
  // Everyone: their own figures at least (Plan X2); the page shows more by permission.
  { key: 'dashboard', path: paths.dashboard, icon: PieChart, label: 'Thống kê' },
  { key: 'docs', path: paths.docs, icon: BookMarked, label: 'Tài liệu', membersOnly: true },
  { key: 'posts', path: paths.posts, icon: FileText, label: 'Bài viết', permission: 'posts.manage' },
  { key: 'mail', path: paths.mail, icon: Mail, label: 'Gửi mail', permission: 'mail.campaigns' },
  { key: 'generations', path: paths.generations, icon: GitBranch, label: 'Gen & bàn giao', permission: 'generations.manage' },
  // The club's settings only (Plan BI): a member's own account is the dialog in the account menu.
  { key: 'settings', path: paths.settings, icon: Settings, label: 'Cài đặt', anyPermission: SETTINGS_PERMISSIONS },
  { key: 'logs', path: paths.logs, icon: List, label: 'Nhật ký', permission: 'logs.view' },
];

export const navFor = (can: (p: Permission) => boolean, isAlumni = false): NavItem[] =>
  NAV_ITEMS.filter((i) => (!i.permission || can(i.permission)) && (!i.anyPermission || i.anyPermission.some(can)) && !(isAlumni && i.membersOnly));

/**
 * Anchors on the club landing page shown in its header. Hoạt động and Liên hệ
 * left the menu (28/09/2026); their sections stay on the page, and #activities
 * and #contact still scroll to them.
 */
export const homeAnchors = [
  { id: 'about', label: 'Về HBAC' },
  { id: 'news', label: 'Tin tức' },
  { id: 'join', label: 'Tuyển thành viên' },
] as const;
